// frontend/src/pages/AdminDashboard.js

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Nav } from 'react-bootstrap';
import { AdminService } from "../services";
import AccessRequestService from "../services/accessRequest.service";
import './AdminDashboard.css';

const AdminDashboard = (props) => {
  const [dashboardStats, setDashboardStats] = useState({
    totalUsers: 0,
    onlineUsers: 0,
    frozenUsers: 0,
    pendingRecharges: 0,
    pendingWithdrawals: 0,
    pendingTrades: 0,
    totalBalance: 0
  });

  const [activeSection, setActiveSection] = useState('dashboard');
  const [users, setUsers] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deductionAmount, setDeductionAmount] = useState('');
  const [deductionReason, setDeductionReason] = useState('');
  const [messageTitle, setMessageTitle] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [balanceMode, setBalanceMode] = useState('deduct');
  const [recharges, setRecharges] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processType, setProcessType] = useState('');
  const [processAction, setProcessAction] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  const [accessRequests, setAccessRequests] = useState([]);
  const [apiErrors, setApiErrors] = useState({});
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [freezeMode, setFreezeMode] = useState('freeze'); // 'freeze' or 'unfreeze'
  const [freezeAmount, setFreezeAmount] = useState('');
  const [freezeReason, setFreezeReason] = useState('');
  const [showCreditScoreModal, setShowCreditScoreModal] = useState(false);
  const [creditScore, setCreditScore] = useState('');
  const [creditScoreReason, setCreditScoreReason] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showMessageModal, setShowMessageModal] = useState(false);

  const getCurrentAdmin = useCallback(() => {
    try {
      const adminData = localStorage.getItem('adminUser');
      if (!adminData) return null;
      const admin = JSON.parse(adminData);
      return (admin.accessToken || admin.token) ? admin : null;
    } catch (error) {
      return null;
    }
  }, []);

  // SAFE API CALLS WITH ERROR HANDLING
  const loadDashboardData = useCallback(() => {
    setLoading(true);
    AdminService.getAdminDashboard()
      .then((response) => {
        setDashboardStats(response.data || {});
        setLoading(false);
      })
      .catch((error) => {
        console.error("Dashboard load error:", error);
        setLoading(false);
        if (error.response?.status === 401) {
          localStorage.removeItem('adminUser');
          localStorage.removeItem('adminToken');
          props.history.push("/login");
        }
      });
  }, [props.history]);

  const loadUsers = useCallback(() => {
    AdminService.getAllUsers()
      .then((response) => {
        setUsers(Array.isArray(response?.data) ? response.data : []);
      })
      .catch((error) => {
        console.error("Users load error:", error);
        setUsers([]);
      });
  }, []);

  const loadRecharges = useCallback(() => {
    AdminService.getAllRecharges()
      .then((response) => {
        setRecharges(Array.isArray(response?.data) ? response.data : []);
      })
      .catch((error) => {
        console.error("Recharges load error:", error);
        setRecharges([]);
      });
  }, []);

  const loadWithdrawals = useCallback(() => {
    AdminService.getAllWithdrawals()
      .then((response) => {
        setWithdrawals(Array.isArray(response?.data) ? response.data : []);
      })
      .catch((error) => {
        console.error("Withdrawals load error:", error);
        setWithdrawals([]);
      });
  }, []);

  const loadAccessRequests = useCallback(() => {
    AccessRequestService.getAllRequests()
      .then((response) => {
        // DOUBLE SAFETY CHECK
        if (response && response.data && Array.isArray(response.data)) {
          setAccessRequests(response.data);
          setApiErrors(prev => ({ ...prev, accessRequests: null }));
        } else {
          console.warn("Access requests returned invalid format:", response);
          setAccessRequests([]);
        }
      })
      .catch((error) => {
        console.error("Access requests load error:", error);
        setAccessRequests([]);
        setApiErrors(prev => ({ ...prev, accessRequests: "Failed to load access requests" }));
      });
  }, []);

  useEffect(() => {
    const currentAdmin = getCurrentAdmin();
    if (!currentAdmin) {
      props.history.push("/login");
      return;
    }
    
    // Load data with delay to prevent race conditions
    const loadData = async () => {
      try {
        await Promise.allSettled([
          loadDashboardData(),
          loadUsers(),
          loadRecharges(),
          loadWithdrawals(),
          loadAccessRequests()
        ]);
      } catch (error) {
        console.error("Initial load error:", error);
      }
    };
    
    loadData();
  }, [props.history, getCurrentAdmin, loadDashboardData, loadUsers, loadRecharges, loadWithdrawals, loadAccessRequests]);

  // Helper function - SAFE filtering
  const getPendingAccessRequestsCount = () => {
    if (!Array.isArray(accessRequests)) return 0;
    return accessRequests.filter(r => r && r.status === 'pending').length;
  };

  // Rest of your existing functions remain the same...
  const handleLogout = () => {
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminSession');
    props.history.push("/login");
  };

  const toggleUserFreeze = (userId) => {
    AdminService.toggleUserFreeze(userId)
      .then((response) => {
        alert("User status updated successfully");
        loadUsers();
        loadDashboardData();
      })
      .catch((error) => {
        alert("Failed to update user status: " + (error.response?.data?.message || error.message));
      });
  };

  const setUserOffline = (userId) => {
    AdminService.setUserOffline(userId)
      .then((response) => {
        alert("User set to offline successfully");
        loadUsers();
        loadDashboardData();
      })
      .catch((error) => {
        alert("Failed to set user offline");
      });
  };

  const openBalanceModal = (user, mode) => {
    setSelectedUser(user);
    setBalanceMode(mode);
    setDeductionAmount('');
    setDeductionReason('');
    setShowUserModal(true);
  };

  const openFreezeModal = (user, mode) => {
    setSelectedUser(user);
    setFreezeMode(mode);
    setFreezeAmount('');
    setFreezeReason('');
    setShowFreezeModal(true);
  };

  const handleFreezeBalance = () => {
    if (!freezeAmount || freezeAmount <= 0) {
      alert("Please enter a valid amount");
      return;
    }
    if (!selectedUser) {
      alert("No user selected");
      return;
    }

    const apiCall = freezeMode === 'freeze' ? AdminService.freezeBalance : AdminService.unfreezeBalance;
    const actionName = freezeMode === 'freeze' ? 'frozen' : 'unfrozen';

    apiCall(selectedUser._id, parseFloat(freezeAmount), freezeReason)
      .then((response) => {
        alert(`Amount ${actionName} successfully`);
        setShowFreezeModal(false);
        setFreezeAmount('');
        setFreezeReason('');
        setSelectedUser(null);
        loadDashboardData();
        loadUsers();
      })
      .catch((error) => {
        alert(`Failed to ${freezeMode} balance: ` + (error.response?.data?.message || error.message));
      });
  };

  const openCreditScoreModal = (user) => {
    setSelectedUser(user);
    setCreditScore(user.creditScore || 100);
    setCreditScoreReason('');
    setShowCreditScoreModal(true);
  };

  const handleCreditScoreUpdate = () => {
    if (!creditScore || creditScore < 0 || creditScore > 1000) {
      alert("Please enter a valid credit score (0-1000)");
      return;
    }
    if (!selectedUser) {
      alert("No user selected");
      return;
    }

    AdminService.updateCreditScore(selectedUser._id, parseInt(creditScore), creditScoreReason)
      .then((response) => {
        alert(`Credit score updated to ${creditScore} successfully`);
        setShowCreditScoreModal(false);
        setCreditScore('');
        setCreditScoreReason('');
        setSelectedUser(null);
        loadUsers();
      })
      .catch((error) => {
        alert(`Failed to update credit score: ` + (error.response?.data?.message || error.message));
      });
  };

  const handleBalanceUpdate = () => {
    if (!deductionAmount || deductionAmount <= 0) {
      alert("Please enter a valid amount");
      return;
    }
    if (!selectedUser) {
      alert("No user selected");
      return;
    }

    const apiCall = balanceMode === 'add' ? AdminService.addToUser : AdminService.deductFromUser;
    const actionName = balanceMode === 'add' ? 'added' : 'deducted';

    apiCall(selectedUser._id, parseFloat(deductionAmount), deductionReason)
      .then((response) => {
        alert(`Amount ${actionName} successfully`);
        setShowUserModal(false);
        setDeductionAmount('');
        setDeductionReason('');
        setSelectedUser(null);
        loadDashboardData();
        loadUsers();
      })
      .catch((error) => {
        alert(`Failed to ${balanceMode} amount: ` + (error.response?.data?.message || error.message));
      });
  };

  const sendMessageToUser = (user) => {
    if (!messageTitle || !messageContent) {
      alert("Please enter both title and message");
      return;
    }

    AdminService.sendMessageToUser(user._id, messageTitle, messageContent)
      .then((response) => {
        alert("Message sent successfully to " + user.username);
        setMessageTitle('');
        setMessageContent('');
        setSelectedUser(null);
        setShowMessageModal(false);
      })
      .catch((error) => {
        alert("Failed to send message: " + (error.response?.data?.message || error.message));
      });
  };

  const openProcessModal = (item, type, action) => {
    setSelectedRequest(item);
    setProcessType(type);
    setProcessAction(action);
    setAdminNote('');
    setShowProcessModal(true);
  };

  const handleProcessRequest = () => {
    if (!selectedRequest) return;

    const apiCall = processType === 'recharge'
      ? (processAction === 'approve' ? AdminService.approveRecharge : AdminService.rejectRecharge)
      : (processAction === 'approve' ? AdminService.approveWithdrawal : AdminService.rejectWithdrawal);

    apiCall(selectedRequest._id, adminNote)
      .then(() => {
        alert(`${processType === 'recharge' ? 'Recharge' : 'Withdrawal'} ${processAction}ed successfully`);
        setShowProcessModal(false);
        if (processType === 'recharge') loadRecharges();
        else loadWithdrawals();
        loadDashboardData();
      })
      .catch(error => {
        alert(`Failed to ${processAction} request: ` + (error.response?.data?.message || error.message));
      });
  };

  const handleAccessRequestStatus = (id, status) => {
    if (!window.confirm(`Are you sure you want to ${status} this request?`)) return;

    AccessRequestService.updateRequestStatus(id, status)
      .then((response) => {
        alert(`Request ${status} successfully`);
        loadAccessRequests();
        loadUsers();
      })
      .catch(error => {
        alert(`Failed to ${status} request: ` + (error.response?.data?.message || error.message));
      });
  };

  const renderDashboard = () => (
    <div className="admin-dashboard">
      <h2 className="mb-4 text-dark">Admin Dashboard</h2>
      
      {apiErrors.accessRequests && (
        <div className="alert alert-warning">
          <strong>Note:</strong> {apiErrors.accessRequests}
        </div>
      )}

      <Row className="mb-4">
        <Col md={2} sm={4} xs={6}>
          <Card className="stats-card">
            <Card.Body className="text-center">
              <div className="stats-icon users">👥</div>
              <h3 className="text-dark mb-2">{dashboardStats.totalUsers}</h3>
              <p className="text-muted mb-0">Total Users</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2} sm={4} xs={6}>
          <Card className="stats-card">
            <Card.Body className="text-center">
              <div className="stats-icon online">🟢</div>
              <h3 className="text-dark mb-2">{dashboardStats.onlineUsers}</h3>
              <p className="text-muted mb-0">Online Users</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2} sm={4} xs={6}>
          <Card className="stats-card">
            <Card.Body className="text-center">
              <div className="stats-icon frozen">❄️</div>
              <h3 className="text-dark mb-2">{dashboardStats.frozenUsers}</h3>
              <p className="text-muted mb-0">Frozen Users</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2} sm={4} xs={6}>
          <Card className="stats-card">
            <Card.Body className="text-center">
              <div className="stats-icon recharge">💰</div>
              <h3 className="text-dark mb-2">{dashboardStats.pendingRecharges}</h3>
              <p className="text-muted mb-0">Pending Recharges</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2} sm={4} xs={6}>
          <Card className="stats-card">
            <Card.Body className="text-center">
              <div className="stats-icon withdrawal">💸</div>
              <h3 className="text-dark mb-2">{dashboardStats.pendingWithdrawals}</h3>
              <p className="text-muted mb-0">Pending Withdrawals</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2} sm={4} xs={6}>
          <Card className="stats-card">
            <Card.Body className="text-center">
              <div className="stats-icon trades">📊</div>
              <h3 className="text-dark mb-2">{dashboardStats.pendingTrades}</h3>
              <p className="text-muted mb-0">Pending Trades</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={3}>
          <Card className="stats-card">
            <Card.Body className="text-center">
              <div className="stats-icon requests">📝</div>
              <h3 className="text-dark mb-2">{getPendingAccessRequestsCount()}</h3>
              <p className="text-muted mb-0">Pending Access Requests</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-primary text-white">
          <h4 className="mb-0">Platform Statistics</h4>
        </Card.Header>
        <Card.Body className="bg-light">
          <Row>
            <Col md={6}>
              <div className="stat-item mb-3">
                <strong className="text-dark">Total Platform Balance:</strong>
                <span className="text-success fw-bold ms-2">{(dashboardStats.totalBalance || 0).toFixed(2)}</span>
              </div>
            </Col>
            <Col md={6}>
              <div className="stat-item mb-3">
                <strong className="text-dark">Active Trading Sessions:</strong>
                <span className="text-info fw-bold ms-2">{dashboardStats.pendingTrades}</span>
              </div>
            </Col>
            <Col md={6}>
              <div className="stat-item mb-3">
                <strong className="text-dark">Total Users:</strong>
                <span className="text-primary fw-bold ms-2">{dashboardStats.totalUsers}</span>
              </div>
            </Col>
            <Col md={6}>
              <div className="stat-item mb-3">
                <strong className="text-dark">Online Users:</strong>
                <span className="text-success fw-bold ms-2">{dashboardStats.onlineUsers}</span>
              </div>
            </Col>
            <Col md={6}>
              <div className="stat-item mb-3">
                <strong className="text-dark">Frozen Users:</strong>
                <span className="text-warning fw-bold ms-2">{dashboardStats.frozenUsers}</span>
              </div>
            </Col>
            <Col md={6}>
              <div className="stat-item mb-3">
                <strong className="text-dark">Pending Recharges:</strong>
                <span className="text-danger fw-bold ms-2">{dashboardStats.pendingRecharges}</span>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );

  const renderUserManagement = () => {
    // Filter users based on search query (username or email)
    const filteredUsers = !Array.isArray(users) ? [] : users.filter(user => {
      if (!userSearchQuery.trim()) return true;
      const query = userSearchQuery.toLowerCase().trim();
      const username = (user.username || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      return username.includes(query) || email.includes(query);
    });

    return (
      <div className="user-management">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0 text-dark">User Management</h2>
          <div style={{ width: '300px' }}>
            <Form.Control
              type="text"
              placeholder="Search by username or email..."
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              style={{
                backgroundColor: 'white',
                color: 'black',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '8px 12px'
              }}
            />
          </div>
        </div>

        {filteredUsers.length === 0 ? (
        <div className="text-center py-5">
          <h4 className="text-muted">
            {userSearchQuery.trim() ? 'No users found matching your search' : 'No users found'}
          </h4>
          <p className="text-muted">
            {userSearchQuery.trim() 
              ? `No users match "${userSearchQuery}". Try a different search term.`
              : 'There are no users in the system yet.'}
          </p>
          {userSearchQuery.trim() && (
            <Button 
              variant="secondary" 
              size="sm" 
              onClick={() => setUserSearchQuery('')}
              className="mt-2"
            >
              Clear Search
            </Button>
          )}
        </div>
      ) : (
        <>
          <Table striped bordered hover responsive className="users-table">
          <thead className="table-dark">
            <tr>
              <th className="text-white">Username</th>
              <th className="text-white">Email</th>
              <th className="text-white">Total Balance</th>
              <th className="text-white">Available</th>
              <th className="text-white">Frozen</th>
              <th className="text-white">Status</th>
              <th className="text-white">Account</th>
              <th className="text-white">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => {
              const totalBalance = user.balance || 0;
              const frozenBalance = user.frozenBalance || 0;
              const availableBalance = totalBalance - frozenBalance;
              
              return (
                <tr key={user._id}>
                  <td className="text-dark">{user.username}</td>
                  <td className="text-dark">{user.email}</td>
                  <td className="text-success fw-bold">{totalBalance.toFixed(2)}</td>
                  <td className="text-primary fw-bold">{availableBalance.toFixed(2)}</td>
                  <td className="text-warning fw-bold">{frozenBalance.toFixed(2)}</td>
                  <td>
                    <span className={`status-badge ${user.isOnline ? 'online' : 'offline'}`}>
                      {user.isOnline ? '🟢 Online' : '🔴 Offline'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.isFrozen ? 'frozen' : 'active'}`}>
                      {user.isFrozen ? '❄️ Frozen' : '✅ Active'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Button
                        size="sm"
                        variant={user.isFrozen ? "success" : "warning"}
                        onClick={() => toggleUserFreeze(user._id)}
                        className="me-1 mb-1"
                      >
                        {user.isFrozen ? 'Unfreeze' : 'Freeze'}
                      </Button>

                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setUserOffline(user._id)}
                        disabled={!user.isOnline}
                        className="me-1 mb-1"
                      >
                        Set Offline
                      </Button>

                      <Button
                        size="sm"
                        variant="success"
                        onClick={() => openBalanceModal(user, 'add')}
                        className="me-1 mb-1"
                      >
                        Add
                      </Button>

                      <Button
                        size="sm"
                        variant="info"
                        onClick={() => openBalanceModal(user, 'deduct')}
                        className="me-1 mb-1"
                      >
                        Deduct
                      </Button>

                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => openFreezeModal(user, 'freeze')}
                        disabled={availableBalance <= 0}
                        className="me-1 mb-1"
                        title={availableBalance <= 0 ? 'No available balance to freeze' : 'Freeze Balance'}
                      >
                        🔒 Freeze Bal
                      </Button>

                      <Button
                        size="sm"
                        variant="warning"
                        onClick={() => openFreezeModal(user, 'unfreeze')}
                        disabled={frozenBalance <= 0}
                        className="me-1 mb-1"
                        title={frozenBalance <= 0 ? 'No frozen balance to unfreeze' : 'Unfreeze Balance'}
                      >
                        🔓 Unfreeze Bal
                      </Button>

                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => openCreditScoreModal(user)}
                        className="me-1 mb-1"
                        title="Update Credit Score"
                      >
                        ⭐ Credit Score
                      </Button>

                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => {
                          setSelectedUser(user);
                          setMessageTitle('Admin Message');
                          setMessageContent('');
                          setShowMessageModal(true);
                        }}
                        className="mb-1"
                      >
                        Message
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {userSearchQuery.trim() && (
          <div className="mt-3 text-muted text-center">
            Showing {filteredUsers.length} of {users.length} users
          </div>
        )}
        </>
      )}
      </div>
    );
  };

  const renderRechargeManagement = () => (
    <div className="recharge-management">
      <h2 className="mb-4 text-dark">Recharge Management</h2>
      <Table striped bordered hover responsive>
        <thead className="table-dark">
          <tr>
            <th className="text-white">User</th>
            <th className="text-white">Amount</th>
            <th className="text-white">Channel</th>
            <th className="text-white">Status</th>
            <th className="text-white">Date</th>
            <th className="text-white">Actions</th>
          </tr>
        </thead>
        <tbody>
          {Array.isArray(recharges) && recharges.map(item => (
            <tr key={item._id}>
              <td className="text-dark">{item.username}</td>
              <td className="text-success fw-bold">{item.amount}</td>
              <td className="text-dark">{item.channel}</td>
              <td>
                <span className={`badge bg-${item.status === 'Approved' ? 'success' : item.status === 'Rejected' ? 'danger' : 'warning'}`}>
                  {item.status}
                </span>
              </td>
              <td className="text-dark">{new Date(item.createdAt).toLocaleString()}</td>
              <td>
                {item.status === 'Pending' && (
                  <>
                    <Button size="sm" variant="success" className="me-2" onClick={() => openProcessModal(item, 'recharge', 'approve')}>Approve</Button>
                    <Button size="sm" variant="danger" onClick={() => openProcessModal(item, 'recharge', 'reject')}>Reject</Button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );

  const renderWithdrawalManagement = () => (
    <div className="withdrawal-management">
      <h2 className="mb-4 text-dark">Withdrawal Management</h2>
      <Table striped bordered hover responsive>
        <thead className="table-dark">
          <tr>
            <th className="text-white">User</th>
            <th className="text-white">Amount</th>
            <th className="text-white">Wallet</th>
            <th className="text-white">Status</th>
            <th className="text-white">Date</th>
            <th className="text-white">Actions</th>
          </tr>
        </thead>
        <tbody>
          {Array.isArray(withdrawals) && withdrawals.map(item => (
            <tr key={item._id}>
              <td className="text-dark">{item.username}</td>
              <td className="text-danger fw-bold">-{item.amount}</td>
              <td className="text-dark">
                {item.walletType === 'bank' ? '🏦 Bank' : '₿ Digital'}
                <br />
                <small className="text-muted">
                  {item.walletType === 'bank'
                    ? `${item.walletDetails?.bankName} - ${item.walletDetails?.accountNo}`
                    : `${item.walletDetails?.currency} - ${item.walletDetails?.address}`
                  }
                </small>
              </td>
              <td>
                <span className={`badge bg-${item.status === 'Approved' ? 'success' : item.status === 'Rejected' ? 'danger' : 'warning'}`}>
                  {item.status}
                </span>
              </td>
              <td className="text-dark">{new Date(item.createdAt).toLocaleString()}</td>
              <td>
                {item.status === 'Pending' && (
                  <>
                    <Button size="sm" variant="success" className="me-2" onClick={() => openProcessModal(item, 'withdrawal', 'approve')}>Approve</Button>
                    <Button size="sm" variant="danger" onClick={() => openProcessModal(item, 'withdrawal', 'reject')}>Reject</Button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );

  const renderAccessRequests = () => (
    <div className="access-requests-management">
      <h2 className="mb-4 text-dark">Access Requests</h2>
      
      {apiErrors.accessRequests && (
        <div className="alert alert-warning mb-3">
          <strong>Warning:</strong> {apiErrors.accessRequests}
        </div>
      )}
      
      {!Array.isArray(accessRequests) || accessRequests.length === 0 ? (
        <div className="text-center py-5">
          <h4 className="text-muted">No access requests found</h4>
          <p className="text-muted">There are no pending or processed access requests.</p>
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th className="text-white">Username</th>
              <th className="text-white">Email</th>
              <th className="text-white">Request Type</th>
              <th className="text-white">Status</th>
              <th className="text-white">Requested At</th>
              <th className="text-white">Actions</th>
            </tr>
          </thead>
          <tbody>
            {accessRequests.map(request => (
              <tr key={request._id}>
                <td className="text-dark">{request.username}</td>
                <td className="text-dark">{request.email}</td>
                <td className="text-dark">{request.requestType}</td>
                <td>
                  <span className={`badge bg-${request.status === 'approved' ? 'success' : request.status === 'rejected' ? 'danger' : 'warning'}`}>
                    {request.status}
                  </span>
                </td>
                <td className="text-dark">
                  {request.requestedAt 
                    ? new Date(request.requestedAt).toLocaleString() 
                    : request.createdAt 
                      ? new Date(request.createdAt).toLocaleString() 
                      : 'N/A'}
                </td>
                <td>
                  {request.status === 'pending' && (
                    <>
                      <Button size="sm" variant="success" className="me-2" onClick={() => handleAccessRequestStatus(request._id, 'accepted')}>Approve</Button>
                      <Button size="sm" variant="danger" onClick={() => handleAccessRequestStatus(request._id, 'rejected')}>Reject</Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );

  const currentAdmin = getCurrentAdmin();

  return (
    <Container fluid className="admin-container">
      <div className="admin-header bg-dark text-white">
        <div className="d-flex justify-content-between align-items-center p-3">
          <h1 className="mb-0 text-white">SuperCoin Admin Panel</h1>
          <div>
            <span className="me-3 text-white">Welcome, {currentAdmin?.username || 'Admin'}</span>
            <Button variant="outline-light" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </div>

      <Nav variant="tabs" className="admin-nav bg-light">
        <Nav.Item>
          <Nav.Link
            active={activeSection === 'dashboard'}
            onClick={() => setActiveSection('dashboard')}
            className={activeSection === 'dashboard' ? 'active text-dark' : 'text-dark'}
          >
            📊 Dashboard
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={activeSection === 'users'}
            onClick={() => setActiveSection('users')}
            className={activeSection === 'users' ? 'active text-dark' : 'text-dark'}
          >
            👥 User Management
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={activeSection === 'recharges'}
            onClick={() => setActiveSection('recharges')}
            className={activeSection === 'recharges' ? 'active text-dark' : 'text-dark'}
          >
            💰 Recharge Management
            {dashboardStats.pendingRecharges > 0 && (
              <span className="badge bg-danger ms-2">{dashboardStats.pendingRecharges}</span>
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={activeSection === 'withdrawals'}
            onClick={() => setActiveSection('withdrawals')}
            className={activeSection === 'withdrawals' ? 'active text-dark' : 'text-dark'}
          >
            💸 Withdrawal Management
            {dashboardStats.pendingWithdrawals > 0 && (
              <span className="badge bg-danger ms-2">{dashboardStats.pendingWithdrawals}</span>
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={activeSection === 'trades'}
            onClick={() => setActiveSection('trades')}
            className={activeSection === 'trades' ? 'active text-dark' : 'text-dark'}
          >
            📊 Trade Management
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={activeSection === 'accessRequests'}
            onClick={() => setActiveSection('accessRequests')}
            className={activeSection === 'accessRequests' ? 'active text-dark' : 'text-dark'}
          >
            📝 Access Requests
            {getPendingAccessRequestsCount() > 0 && (
              <span className="badge bg-danger ms-2">{getPendingAccessRequestsCount()}</span>
            )}
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <div className="admin-content p-4 bg-white">
        {activeSection === 'dashboard' && renderDashboard()}
        {activeSection === 'users' && renderUserManagement()}
        {activeSection === 'recharges' && renderRechargeManagement()}
        {activeSection === 'withdrawals' && renderWithdrawalManagement()}
        {activeSection === 'trades' && <div className="text-center py-5"><h3 className="text-dark">Trade Management</h3><p className="text-muted">Trade management features are coming soon.</p></div>}
        {activeSection === 'accessRequests' && renderAccessRequests()}
      </div>

      {/* Balance Modal */}
      <Modal show={showUserModal} onHide={() => setShowUserModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-dark">{balanceMode === 'add' ? 'Add to User Balance' : 'Deduct from User'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <p className="text-dark">User: <strong className="text-primary">{selectedUser.username}</strong></p>
              <p className="text-dark">Current Balance: <strong className="text-success">{(selectedUser.balance || 0).toFixed(2)}</strong></p>

              <Form.Group className="mb-3">
                <Form.Label className="text-dark">Amount to {balanceMode === 'add' ? 'Add' : 'Deduct'}</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter amount"
                  value={deductionAmount}
                  onChange={(e) => setDeductionAmount(e.target.value)}
                  min="0"
                  step="0.01"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="text-dark">Reason</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder={`Enter reason for ${balanceMode === 'add' ? 'addition' : 'deduction'}`}
                  value={deductionReason}
                  onChange={(e) => setDeductionReason(e.target.value)}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUserModal(false)}>
            Cancel
          </Button>
          <Button variant={balanceMode === 'add' ? 'success' : 'danger'} onClick={handleBalanceUpdate}>
            {balanceMode === 'add' ? 'Add Amount' : 'Deduct Amount'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Message Modal */}
      <Modal show={showMessageModal} onHide={() => { setShowMessageModal(false); setSelectedUser(null); }} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-dark">Send Message to {selectedUser?.username}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="text-dark">Message Title</Form.Label>
            <Form.Control
              type="text"
              value={messageTitle}
              onChange={(e) => setMessageTitle(e.target.value)}
              placeholder="Enter message title"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="text-dark">Message Content</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              placeholder="Enter your message..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowMessageModal(false); setSelectedUser(null); }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => sendMessageToUser(selectedUser)}>
            Send Message
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Process Request Modal */}
      <Modal show={showProcessModal} onHide={() => setShowProcessModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-dark">{processAction === 'approve' ? 'Approve' : 'Reject'} {processType === 'recharge' ? 'Recharge' : 'Withdrawal'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-dark">Are you sure you want to {processAction} this request?</p>
          {selectedRequest && (
            <div className="alert alert-info">
              <strong className="text-dark">User:</strong> <span className="text-dark">{selectedRequest.username}</span><br />
              <strong className="text-dark">Amount:</strong> <span className="text-dark">{selectedRequest.amount}</span>
            </div>
          )}
          <Form.Group>
            <Form.Label className="text-dark">Admin Note (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="Enter reason or note..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowProcessModal(false)}>Cancel</Button>
          <Button 
            variant={processAction === 'approve' ? 'success' : 'danger'}
            onClick={handleProcessRequest}
          >
            Confirm {processAction === 'approve' ? 'Approval' : 'Rejection'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Freeze/Unfreeze Balance Modal */}
      <Modal show={showFreezeModal} onHide={() => setShowFreezeModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-dark">{freezeMode === 'freeze' ? 'Freeze' : 'Unfreeze'} Balance</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <p className="text-dark">User: <strong className="text-primary">{selectedUser.username}</strong></p>
              <div className="alert alert-info">
                <strong className="text-dark">Total Balance:</strong> <span className="text-success">{((selectedUser.balance || 0)).toFixed(2)}</span><br />
                <strong className="text-dark">Available Balance:</strong> <span className="text-primary">{((selectedUser.balance || 0) - (selectedUser.frozenBalance || 0)).toFixed(2)}</span><br />
                <strong className="text-dark">Frozen Balance:</strong> <span className="text-warning">{((selectedUser.frozenBalance || 0)).toFixed(2)}</span>
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="text-dark">Amount to {freezeMode === 'freeze' ? 'Freeze' : 'Unfreeze'}</Form.Label>
                <Form.Control
                  type="number"
                  placeholder={`Enter amount (Max: ${freezeMode === 'freeze' ? ((selectedUser.balance || 0) - (selectedUser.frozenBalance || 0)).toFixed(2) : ((selectedUser.frozenBalance || 0)).toFixed(2)})`}
                  value={freezeAmount}
                  onChange={(e) => setFreezeAmount(e.target.value)}
                  min="0"
                  max={freezeMode === 'freeze' ? (selectedUser.balance || 0) - (selectedUser.frozenBalance || 0) : (selectedUser.frozenBalance || 0)}
                  step="0.01"
                />
                <Form.Text className="text-muted">
                  {freezeMode === 'freeze' 
                    ? `Maximum available to freeze: ${((selectedUser.balance || 0) - (selectedUser.frozenBalance || 0)).toFixed(2)}`
                    : `Maximum frozen balance: ${((selectedUser.frozenBalance || 0)).toFixed(2)}`
                  }
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="text-dark">Reason</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder={`Enter reason for ${freezeMode === 'freeze' ? 'freezing' : 'unfreezing'} balance`}
                  value={freezeReason}
                  onChange={(e) => setFreezeReason(e.target.value)}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFreezeModal(false)}>
            Cancel
          </Button>
          <Button variant={freezeMode === 'freeze' ? 'danger' : 'warning'} onClick={handleFreezeBalance}>
            {freezeMode === 'freeze' ? 'Freeze Balance' : 'Unfreeze Balance'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Credit Score Modal */}
      <Modal show={showCreditScoreModal} onHide={() => setShowCreditScoreModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-dark">Update Credit Score</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <p className="text-dark mb-3">
                <strong>User:</strong> {selectedUser.username} <br />
                <strong>Current Credit Score:</strong> {selectedUser.creditScore || 100}
              </p>
              <Form.Group>
                <Form.Label className="text-dark">New Credit Score (0-1000)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="1000"
                  placeholder="Enter credit score"
                  value={creditScore}
                  onChange={(e) => setCreditScore(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Enter a value between 0 and 1000
                </Form.Text>
              </Form.Group>
              <Form.Group className="mt-3">
                <Form.Label className="text-dark">Reason (Optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter reason for credit score update"
                  value={creditScoreReason}
                  onChange={(e) => setCreditScoreReason(e.target.value)}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreditScoreModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleCreditScoreUpdate}>
            Update Credit Score
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminDashboard;
